import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api } from "../../lib/api";

const statusOptions = ["published", "draft", "archived"];

function formatFileSize(value) {
  const bytes = Number(value || 0);
  if (!bytes) return "Unknown size";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminDownloadList() {
  const [downloads, setDownloads] = useState([]);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setError("");
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (status) params.set("status", status);
    try {
      const data = await api(`/api/admin/downloads${params.toString() ? `?${params}` : ""}`);
      setDownloads(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setDownloads([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const stats = useMemo(() => {
    return downloads.reduce(
      (acc, item) => {
        acc.total += 1;
        acc[item.status] = (acc[item.status] || 0) + 1;
        return acc;
      },
      { total: 0, published: 0, draft: 0, archived: 0 }
    );
  }, [downloads]);

  async function updateStatus(id, nextStatus) {
    setMessage("");
    setError("");
    try {
      await api(`/api/admin/downloads/${id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      setMessage("Download status updated.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function deleteDownload(id) {
    if (!window.confirm("Delete this download permanently?")) return;
    setMessage("");
    setError("");
    try {
      await api(`/api/admin/downloads/${id}`, { method: "DELETE" });
      setMessage("Download deleted.");
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <AdminLayout
      title="Download Center CMS"
      subtitle="Upload and manage academic calendars, forms, timetables, handbooks and official documents."
      actions={<Link to="/adxm/downloads/create" className="rounded-xl bg-emerald-700 px-4 py-3 text-sm font-bold text-white">Add Download</Link>}
    >
      <div className="grid gap-4 md:grid-cols-4">
        {[
          ["Total", stats.total],
          ["Published", stats.published],
          ["Draft", stats.draft],
          ["Archived", stats.archived],
        ].map(([label, value]) => (
          <div key={label} className="rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
            <p className="text-sm font-bold text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-black text-slate-950">{value || 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-3xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
        <div className="grid gap-3 md:grid-cols-[1fr_220px_120px]">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by title, category or description..."
            className="rounded-xl border border-slate-300 px-4 py-3"
          />
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-300 px-4 py-3">
            <option value="">All status</option>
            {statusOptions.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <button onClick={load} className="rounded-xl bg-slate-950 px-4 py-3 font-bold text-white">Search</button>
        </div>
      </div>

      {message && <div className="mt-6 rounded-xl bg-emerald-50 p-4 font-semibold text-emerald-700">{message}</div>}
      {error && <div className="mt-6 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

      <div className="mt-6 overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="p-4">Document</th>
                <th className="p-4">Category</th>
                <th className="p-4">Audience</th>
                <th className="p-4">File</th>
                <th className="p-4">Status</th>
                <th className="p-4">Downloads</th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">Loading downloads...</td></tr>
              ) : downloads.length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-slate-500">No downloads found.</td></tr>
              ) : downloads.map((item) => (
                <tr key={item.id} className="border-t border-slate-100 align-top">
                  <td className="p-4">
                    <p className="font-black text-slate-900">{item.title}</p>
                    {item.description && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{item.description}</p>}
                  </td>
                  <td className="p-4">{item.category}</td>
                  <td className="p-4 capitalize">{item.audience || "all"}</td>
                  <td className="p-4">
                    <a href={item.file_url} target="_blank" rel="noopener noreferrer" className="font-bold text-emerald-700">Open file</a>
                    <p className="mt-1 text-xs text-slate-500">{item.file_type || "file"} · {formatFileSize(item.file_size)}</p>
                  </td>
                  <td className="p-4">
                    <select value={item.status || "draft"} onChange={(e) => updateStatus(item.id, e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-bold">
                      {statusOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </td>
                  <td className="p-4 font-bold">{item.download_count || 0}</td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/adxm/downloads/edit/${item.id}`} className="rounded-lg bg-blue-50 px-3 py-2 text-xs font-bold text-blue-700">Edit</Link>
                      <button onClick={() => deleteDownload(item.id)} className="rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-700">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
